import { useEffect, useState } from 'react';

export type ProductRoute =
  | { kind: 'marketing' }
  | { kind: 'dashboard' }
  | { kind: 'editor'; presentationId: string | null; isNew: boolean }
  | { kind: 'viewer'; presentationId: string }
  | { kind: 'profile' }
  | { kind: 'teams'; teamId: string | null }
  | { kind: 'pricing' }
  | { kind: 'onboarding' }
  | { kind: 'checkoutResult'; result: 'success' | 'cancel' }
  | { kind: 'billing' }
  | { kind: 'authCallback' }
  | { kind: 'auth' }
  | { kind: 'legacy' }
  | { kind: 'notFound'; path: string };

const LEGACY_QUERY_KEYS = new Set([
  'app',
  'pres',
  'presentation',
  'post',
  'embed',
  'storageId',
  'storageid',
  'scene',
]);

function hasLegacyQuery(url: URL): boolean {
  if ([...LEGACY_QUERY_KEYS].some((key) => url.searchParams.has(key))) return true;
  return url.searchParams.get('mode') === 'export';
}

export function resolveProductRoute(location: Pick<Location, 'href'>): ProductRoute {
  const url = new URL(location.href);
  // A share path must never be diverted to legacy loading by ?pres/?embed.
  const publicPath = url.pathname.replace(/\/+$/, '');
  const publicMatch = publicPath.match(/^\/p\/([^/]+)$/);
  if (publicMatch) {
    try { return { kind: 'viewer', presentationId: decodeURIComponent(publicMatch[1]) }; }
    catch { return { kind: 'notFound', path: publicPath }; }
  }
  if (hasLegacyQuery(url)) return { kind: 'legacy' };

  const path = url.pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return { kind: 'marketing' };
  if (path === '/app') return { kind: 'dashboard' };
  if (path === '/edit/new') return { kind: 'editor', presentationId: null, isNew: true };
  if (path === '/profile') return { kind: 'profile' };
  if (path === '/teams') return { kind: 'teams', teamId: null };
  if (path === '/pricing') return { kind: 'pricing' };
  if (path === '/onboarding' || path === '/checkout') return { kind: 'onboarding' };
  if (path === '/checkout/success') return { kind: 'checkoutResult', result: 'success' };
  if (path === '/checkout/cancel') return { kind: 'checkoutResult', result: 'cancel' };
  if (path === '/billing') return { kind: 'billing' };
  if (path === '/auth/callback') return { kind: 'authCallback' };
  if (path === '/auth') return { kind: 'auth' };

  const editorMatch = path.match(/^\/edit\/([^/]+)$/);
  if (editorMatch) {
    return { kind: 'editor', presentationId: decodeURIComponent(editorMatch[1]), isNew: false };
  }

  const teamMatch = path.match(/^\/teams\/([^/]+)$/);
  if (teamMatch) return { kind: 'teams', teamId: decodeURIComponent(teamMatch[1]) };

  return { kind: 'notFound', path };
}

export function navigateProduct(path: string, options: { replace?: boolean } = {}): void {
  const method = options.replace ? 'replaceState' : 'pushState';
  window.history[method]({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useProductRoute(): ProductRoute {
  const [route, setRoute] = useState<ProductRoute>(() => resolveProductRoute(window.location));

  useEffect(() => {
    const update = () => setRoute(resolveProductRoute(window.location));
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);

  return route;
}
